import { Injectable, EventEmitter } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from './authentication.service';
import { HaloToastService, EToastType } from '../halo-toast/halo-toast.service';
import { HaloLoaderService } from '../halo-loader/halo-loader.service';
import { RootScopeService } from '../shared-service/root-scope.service';
import { RootScopeDeclare } from '../rootscope-declare';
import { CommonUtil } from '../Utils/common-util';
import { FacilityService } from './facility.service';
import { CountriesService } from './countries.service';

/*
 * ENUMS
 **/
export enum EActiveTabStatus {
  FORM = 0,
  CONFIRMATION = 1,
}
export enum ESupportType {
  TECHNICIAN_VISIT = '1',
  LF_CONNECT_QUESTION = '2',
  EQUIPMENT_QUESTION = '3',
}
/* export enum ESupportType {
  TECHNICIAN_VISIT = 'technicianvisit',
  LF_CONNECT_QUESTION = 'lfconnectquestion',
  EQUIPMENT_QUESTION = 'equipmentquestion',
} */

/*
 * INTERFACES
 **/
export interface IAttachment {
  file: any;
  name: string;
  size: number;
  mb: string;
  type: string;
}

/*
 * SUPERFOOTER SERVICE
 **/
@Injectable({
  providedIn: 'root',
})
export class SuperFooterService {
  private formDefaultData: any;
  public formSwitch = new EventEmitter();
  attachments: IAttachment[] = [];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private auth: AuthenticationService,
    private rootScopeService: RootScopeService,
    private _facilityService: FacilityService,
    private toast: HaloToastService,
    private loader: HaloLoaderService,
    private u: CommonUtil,
    private _countriesService: CountriesService
  ) { }

  initForm(supportType: ESupportType): FormGroup {
    this.resetAttachments();
    let nonce = this.generateNonce();
    let form: any = this.fb.group({
      supportType: [supportType, [Validators.required]],
      facilityName: ['', [Validators.required]],
      facilityId: ['', [Validators.required]],
      countryId: [''],
      country: [''],
      address: [''],
      state: [''],
      city: [''],
      postalCode: [''],
      supportText: ['', [Validators.required]],
      mgrName: ['', [Validators.required]],
      mgrEmail: ['', [Validators.required]],
      serialNo: [''],
      contactNumber: [''],
      bestTimeToContact: [''],
      nonce: [nonce],
    });

    if (supportType !== ESupportType.LF_CONNECT_QUESTION) {
      form.get('serialNo').setValidators([Validators.required]);
      if (supportType === ESupportType.TECHNICIAN_VISIT) {
        form.get('contactNumber').setValidators([Validators.required]);
        form.get('bestTimeToContact').setValidators([Validators.required]);
      }
    }

    return form;
  }

  updateDefaultDataToFrom(form: FormGroup) {
    // new
    // when facility change
    const _selectedFacility = this._facilityService.selectedFacility();
    this.formDefaultData = {
      facilityName: _selectedFacility.facilityName,
      facilityId: _selectedFacility.facilityId,
      countryId: _selectedFacility.summary.countryId,
      address: _selectedFacility.summary.address,
      state: _selectedFacility.summary.state,
      city: _selectedFacility.summary.city,
      postalCode: _selectedFacility.summary.postalCode,
    };

    form.patchValue(this.formDefaultData);

    // old
    this.rootScopeService.getRootScope().subscribe(
      (res: RootScopeDeclare) => {
        let { currentUser } = res;
        this.formDefaultData = {
          country: this._countriesService.getCountry(this._facilityService.selectedFacility().summary.countryIsoCode),
          mgrName: currentUser ? currentUser.displayName : '',
          mgrEmail: currentUser ? currentUser.emailAddress : '',
        };
        form.patchValue(this.formDefaultData);
      },
      (err: any) => { }
    );
  }

  generateNonce():number {
    let date = new Date(),
      time = date.getTime() / 1000;
    return time + Math.random();
  }

  submit(supportType: ESupportType, data: any, attachments: any[]): Promise<boolean> {
    // start spinner loader
    this.loader.startProcess();
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      const headers = this.auth.authHeader();

      let _Data = JSON.parse(JSON.stringify(data));

      // fixed by jeya
      if (supportType === ESupportType.LF_CONNECT_QUESTION) {
        _Data['supportType'] = 'lfconnectquestion';
      } else if (supportType === ESupportType.EQUIPMENT_QUESTION) {
        _Data['supportType'] = 'equipmentquestion';
      } else {
        _Data['supportType'] = 'technicianvisit';
      }

      // best time to contact
      switch(_Data['bestTimeToContact']) {
        case "0":
          _Data['bestTimeToContact'] = "9 AM to 12 PM"; break;
        case "1":
          _Data['bestTimeToContact'] = "12 PM to 3 PM"; break;
        case "2":
          _Data['bestTimeToContact'] = "3 PM to 6 PM"; break;
      }


      formData.append('data', JSON.stringify(_Data));
      attachments.forEach(attachment => {
        formData.append('contactUsFile', attachment.file);
      });


      this.http
        .post(`${environment.servicePath}/customersupport`, formData, {
          headers: headers,
        })
        .subscribe(
          res => {
            // there is no return from backend so we can only check status code 200/204
            let toastMessage;
            switch (supportType) {
              case ESupportType.LF_CONNECT_QUESTION:
              case ESupportType.EQUIPMENT_QUESTION:
                toastMessage = this.u.replaceValueAsHTMLOnTranslatedText('THANK.YOU1', []);
                break;
              case ESupportType.TECHNICIAN_VISIT:
                toastMessage = this.u.replaceValueAsHTMLOnTranslatedText('THANK.YOU2', []);
                break;
            }
            this.resetAttachments();
            this.toast.creatToast(toastMessage);
            this.loader.endProcess();
            resolve(true);
          },
          (err: any) => {
            console.error('API failed.');
            this.loader.endProcess();
            reject(false);
          }
        );
    });
  }

  resetAttachments() {
    this.attachments = [];
  }

  fileOnChange(files: any) {
    let totalSize = 0;
    let isInvalidFileFormat = false;
    let isMaxSizeExcede = false;
    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        let size = Math.round((files.item(i).size / 1024 / 1024) * 100) / 100;
        this.attachments.push({
          file: files.item(i),
          name: files.item(i).name,
          size: files.item(i).size,
          mb: `${size} MB`,
          type: files.item(i).type,
        });
        totalSize += size;

        // list of mime types
        //https://stackoverflow.com/questions/4212861/what-is-a-correct-mime-type-for-docx-pptx-etc
        if (
          files.item(i).type.match(/image/g) ||
          files.item(i).type.match(/document/g) ||
          files.item(i).type.match(/msword/g) ||
          files.item(i).type.match(/ms-word/g) ||
          files.item(i).type.match(/pdf/g) ||
          files.item(i).type.match(/text/g) ||
          files.item(i).type.match(/ms-excel/g)
        ) {
        } else {
          isInvalidFileFormat = true;
        }

        if (totalSize > 10) {
          isMaxSizeExcede = true;
        }
      }
    }

    return {
      attachments: this.attachments,
      isInvalidFileFormat: isInvalidFileFormat,
      isMaxSizeExcede: isMaxSizeExcede,
    };
  }

  switchForm(value: ESupportType) {
    this.formSwitch.emit(value);
  }
}
